import { z } from 'zod';
import type { HeuristicRuleKind } from '@ent/heuristic-rule';
import type {
  MenuExtraction,
  MenuExtractionIngredient,
  MenuExtractionItem,
  MenuExtractionSpec
} from '../../ai/menu-extraction-schema';

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const INGREDIENT_ROLES = [
  'base_spirit',
  'fortified_wine',
  'amaro_bitter_liqueur',
  'liqueur',
  'sweetener_syrup',
  'citrus_juice',
  'other_juice',
  'dairy_cream',
  'egg_white',
  'whole_egg',
  'carbonated_topper',
  'bitters_dash',
  'muddle_fresh',
  'water_ice',
  'other'
] as const;
export type IngredientRole = (typeof INGREDIENT_ROLES)[number];
export const ingredientRoleSchema = z.enum(INGREDIENT_ROLES);

/** Roles that take part in the role signature used for analog/template matching. */
export const STRUCTURAL_ROLES: readonly IngredientRole[] =
  INGREDIENT_ROLES.filter(role => role !== 'water_ice' && role !== 'other');

export const SPIRIT_FAMILIES = [
  'gin',
  'vodka',
  'rum',
  'whiskey',
  'tequila',
  'mezcal',
  'brandy',
  'other_spirit'
] as const;
export type SpiritFamily = (typeof SPIRIT_FAMILIES)[number];

// ---------------------------------------------------------------------------
// Rule definitions (the JSON stored in `heuristic_rules.definition`)
// ---------------------------------------------------------------------------

export const ingredientRoleDefinitionSchema = z.object({
  role: ingredientRoleSchema,
  /** Word-boundary, case/diacritic-insensitive; the longest alias across all role rules wins. */
  aliases: z.array(z.string().min(1)).default([]),
  /** `base_spirit` only: family -> aliases that imply it. */
  families: z.record(z.string(), z.array(z.string().min(1))).optional(),
  /** Short phrases embedded once to classify names no alias matches. */
  exemplars: z.array(z.string().min(1)).default([]),
  /** FlavorGraph `raw_name`s used as flavor exemplars for the secondary signal. */
  flavorNodes: z.array(z.string().min(1)).default([])
});
export type IngredientRoleDefinition = z.infer<
  typeof ingredientRoleDefinitionSchema
>;

export const templateAmountSchema = z.object({
  amount: z.number().positive(),
  /** A unit `parseMeasure` understands: oz, dash, whole, cube, bsp, drop, ... */
  unit: z.string().min(1)
});
export type TemplateAmount = z.infer<typeof templateAmountSchema>;

export const templateRoleAmountsSchema = z.object({
  /** Consumed in order by the ingredients sharing the role. */
  amounts: z.array(templateAmountSchema).min(1),
  /**
   * When more ingredients share a role than there are entries:
   * `split_total` divides the entries' total equally (default for volumes);
   * `repeat_last` repeats the last entry (default for dash / count units).
   */
  overflow: z.enum(['split_total', 'repeat_last']).optional()
});
export type TemplateRoleAmounts = z.infer<typeof templateRoleAmountsSchema>;

export const proportionTemplateDefinitionSchema = z.object({
  requiredRoles: z.array(ingredientRoleSchema).min(1),
  optionalRoles: z.array(ingredientRoleSchema).default([]),
  amounts: z.partialRecord(ingredientRoleSchema, templateRoleAmountsSchema)
});
export type ProportionTemplateDefinition = z.infer<
  typeof proportionTemplateDefinitionSchema
>;

export const preparationConditionSchema = z.object({
  /** Every listed role must be present. */
  allRoles: z.array(ingredientRoleSchema).optional(),
  /** At least one listed role must be present. */
  anyRoles: z.array(ingredientRoleSchema).optional(),
  /** None of the listed roles may be present. */
  noneRoles: z.array(ingredientRoleSchema).optional(),
  /** Case-insensitive regex over item name + description + instructions. */
  textMatches: z.string().min(1).optional()
});

export const preparationDefinitionSchema = z.object({
  when: preparationConditionSchema,
  preparation: z.string().min(1),
  /** Used only when the extraction left `instructions` blank; `{role}` placeholders are replaced by the ingredient names of that role. */
  instructions: z.string().optional()
});
export type PreparationDefinition = z.infer<typeof preparationDefinitionSchema>;

export type RuleDefinitionByKind = {
  ingredient_role: IngredientRoleDefinition;
  proportion_template: ProportionTemplateDefinition;
  preparation: PreparationDefinition;
};

export function parseRuleDefinition<K extends HeuristicRuleKind>(
  kind: K,
  json: string | unknown
): RuleDefinitionByKind[K] {
  const raw = typeof json === 'string' ? JSON.parse(json) : json;
  switch (kind) {
    case 'ingredient_role':
      return ingredientRoleDefinitionSchema.parse(
        raw
      ) as RuleDefinitionByKind[K];
    case 'proportion_template':
      return proportionTemplateDefinitionSchema.parse(
        raw
      ) as RuleDefinitionByKind[K];
    case 'preparation':
      return preparationDefinitionSchema.parse(raw) as RuleDefinitionByKind[K];
    default:
      throw new Error(`Unknown heuristic rule kind: ${String(kind)}`);
  }
}

/** A rule row after loading: definition parsed, priority numeric. */
export type LoadedRule<K extends HeuristicRuleKind = HeuristicRuleKind> = {
  key: string;
  kind: K;
  name: string;
  priority: number;
  enabled: boolean;
  source: string | null;
  /** Which organization the effective row belongs to (public = system rule). */
  organizationId: string;
  definition: RuleDefinitionByKind[K];
};

export type LoadedRules = {
  roles: LoadedRule<'ingredient_role'>[];
  templates: LoadedRule<'proportion_template'>[];
  preparations: LoadedRule<'preparation'>[];
  /** Keys in effect after org-over-public shadowing, sorted. */
  keys: string[];
};

// ---------------------------------------------------------------------------
// Classification / matching
// ---------------------------------------------------------------------------

export type RoleSource = 'alias' | 'embedding' | 'none';

export type ClassifiedIngredient = {
  name: string;
  normalizedName: string;
  role: IngredientRole;
  family: SpiritFamily | null;
  roleSource: RoleSource;
  /** Combined score for embedding-based assignments (0..1). */
  roleScore?: number;
  flavorNodeId?: string | null;
};

export type RoleMatch = {
  role: IngredientRole;
  score: number;
};

/** Batch nearest-exemplar lookup; `null` entries mean no role cleared the threshold. */
export type NearestExemplars = (
  names: string[]
) => Promise<(RoleMatch | null)[]>;

export type FlavorSignal = {
  enabled: boolean;
  /** Resolve free-text ingredient names to `flavor_nodes.id`s (null when unresolved). */
  resolveNodeIds: (names: string[]) => Promise<(string | null)[]>;
  /** Cosine similarity between two nodes' flavor embeddings (0..1), or null if either is missing. */
  similarity: (nodeIdA: string, nodeIdB: string) => Promise<number | null>;
  /** Mean similarity between a node and a set of nodes, ignoring unresolved ones. */
  similarityToNodes: (
    nodeId: string,
    rawNames: string[]
  ) => Promise<number | null>;
};

export type AnalogIngredient = {
  ingredientId: string;
  name: string;
  role: IngredientRole;
  family: SpiritFamily | null;
  quantity: number;
  quantityType: string;
  flavorNodeId: string | null;
};

export type AnalogRecipe = {
  id: string;
  name: string;
  scope: 'org' | 'public';
  ingredients: AnalogIngredient[];
  /** Optional ranking detail for the trace. */
  flavorScore?: number | null;
};

export type AnalogQuery = {
  signature: string;
  baseFamily: SpiritFamily | null;
  ingredients: ClassifiedIngredient[];
};

export type AnalogFinder = {
  find: (query: AnalogQuery) => Promise<AnalogRecipe | null>;
};

// ---------------------------------------------------------------------------
// Enriched extraction (server-side superset of the LLM schema)
// ---------------------------------------------------------------------------

export type EnrichedIngredient = MenuExtractionIngredient & {
  role?: IngredientRole;
  roleFamily?: SpiritFamily | null;
  quantitySource?: 'printed' | 'analog' | 'template' | 'none';
};

export type EnrichedSpec = Omit<MenuExtractionSpec, 'ingredients'> & {
  ingredients: EnrichedIngredient[];
  preparation?: string | null;
};

export type EnrichedItem = Omit<MenuExtractionItem, 'spec'> & {
  spec: EnrichedSpec | null;
};

export type HeuristicItemTrace = {
  name: string;
  skipped?: 'no_spec' | 'no_ingredients' | 'food' | 'error';
  signature: string;
  baseFamily: SpiritFamily | null;
  ingredients: Array<{
    name: string;
    role: IngredientRole;
    family?: SpiritFamily | null;
    roleSource: RoleSource;
    roleScore?: number;
    flavorNodeId?: string | null;
    quantitySource: 'printed' | 'analog' | 'template' | 'none';
    quantity?: string;
  }>;
  analog?: {
    recipeId: string;
    name: string;
    scope: 'org' | 'public';
    flavorScore?: number | null;
  };
  template?: { key: string };
  preparation?: { key: string; value: string; instructionsFilled: boolean };
  warnings: string[];
};

export type HeuristicTrace = {
  version: 1;
  ruleKeys: string[];
  roleThreshold: number;
  flavorSignal: boolean;
  items: HeuristicItemTrace[];
};

export type EnrichedMenuExtraction = Omit<MenuExtraction, 'items'> & {
  items: EnrichedItem[];
  heuristics?: HeuristicTrace;
};
